import { visionLabApi } from '@/app/apiClient';
import { DEVICE_MANAGEMENT_PAGE_SIZE } from '@/features/device-management/config/deviceManagement';
import type {
  BatchDeleteInput,
  BatchLifecycleInput,
  BatchPatchInput,
  BatchResult,
  CursorPage,
  Device,
  DeviceCreateInput,
  DeviceFieldOption,
  DeviceFieldsCatalog,
  DeviceHistoryEntry,
  DeviceHistoryParams,
  DeviceListParams,
  DevicePatchInput,
  DeviceShipment,
  LifecycleEventInput,
  LifecycleEventReceipt,
  OrganizationOption,
  ShipmentDatesInput,
  ShipmentListParams,
} from '@/features/device-management/types/deviceManagement';
import type { ApiCallOptions } from '@/shared/api/client';
import type { SupportedLocale } from '@/shared/types/i18n';

const devicePath = (deviceId: string) => `/devices/${encodeURIComponent(deviceId)}`;

export function fetchDevices(
  {
    cursor,
    limit = DEVICE_MANAGEMENT_PAGE_SIZE,
    ...params
  }: DeviceListParams & { cursor?: string; limit?: number },
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<CursorPage<Device>>('/devices', {
    ...options,
    searchParams: { ...params, cursor, limit },
  });
}

export function fetchDevice(deviceId: string, timeZone: string, options: ApiCallOptions = {}) {
  return visionLabApi.get<Device>(devicePath(deviceId), { ...options, searchParams: { timeZone } });
}

export function createDevice(body: DeviceCreateInput) {
  return visionLabApi.post<Device>('/devices', { json: body });
}

export function patchDevice(deviceId: string, body: DevicePatchInput) {
  return visionLabApi.patch<Device>(devicePath(deviceId), { json: body });
}

export function deleteDevice(deviceId: string) {
  return visionLabApi.delete<void>(devicePath(deviceId), { responseType: 'void' });
}

export function restoreDevice(deviceId: string, timeZone: string) {
  return visionLabApi.post<Device>(`${devicePath(deviceId)}/restore`, { json: { timeZone } });
}

export function fetchOrganizationOptions(
  params: { operation: 'read' | 'manage'; q?: string; cursor?: string; limit?: number },
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<CursorPage<OrganizationOption>>('/devices/organization-options', {
    ...options,
    searchParams: { ...params, limit: params.limit ?? DEVICE_MANAGEMENT_PAGE_SIZE },
  });
}

export function fetchDeviceFields(locale: SupportedLocale, options: ApiCallOptions = {}) {
  return visionLabApi.get<DeviceFieldsCatalog>('/devices/fields', {
    ...options,
    searchParams: { locale },
  });
}

export function fetchDeviceFieldOptions(
  field: string,
  params: { locale: SupportedLocale; q?: string; cursor?: string; limit?: number },
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<CursorPage<DeviceFieldOption>>(
    `/devices/fields/${encodeURIComponent(field)}/options`,
    { ...options, searchParams: { ...params, limit: params.limit ?? DEVICE_MANAGEMENT_PAGE_SIZE } },
  );
}

export function postLifecycleEvent(deviceId: string, body: LifecycleEventInput) {
  return visionLabApi.post<LifecycleEventReceipt>(`${devicePath(deviceId)}/lifecycle-events`, {
    json: body,
  });
}

export function fetchShipments(
  {
    cursor,
    limit = DEVICE_MANAGEMENT_PAGE_SIZE,
    ...params
  }: ShipmentListParams & { cursor?: string; limit?: number },
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<CursorPage<DeviceShipment>>('/devices/shipments', {
    ...options,
    searchParams: { ...params, cursor, limit },
  });
}

export function fetchDeviceHistory(
  deviceId: string,
  {
    cursor,
    limit = DEVICE_MANAGEMENT_PAGE_SIZE,
    ...params
  }: DeviceHistoryParams & { cursor?: string; limit?: number },
  options: ApiCallOptions = {},
) {
  return visionLabApi.get<CursorPage<DeviceHistoryEntry>>(`${devicePath(deviceId)}/history`, {
    ...options,
    searchParams: { ...params, cursor, limit },
  });
}

export function patchShipmentDates(deviceId: string, shipmentId: string, body: ShipmentDatesInput) {
  return visionLabApi.patch<LifecycleEventReceipt>(
    `${devicePath(deviceId)}/shipments/${encodeURIComponent(shipmentId)}/dates`,
    { json: body },
  );
}

export function batchPatchDevices(body: BatchPatchInput) {
  return visionLabApi.post<BatchResult>('/devices/batch/patch', { json: body });
}

export function batchDeleteDevices(body: BatchDeleteInput) {
  return visionLabApi.post<BatchResult>('/devices/batch/delete', { json: body });
}

export function batchLifecycleEvents(body: BatchLifecycleInput) {
  return visionLabApi.post<BatchResult>('/devices/batch/lifecycle-events', { json: body });
}
